/**
 * Security Configuration
 * Production-ready security settings and policies
 */

// Check if we're in development mode
const isDevelopment = typeof import.meta !== 'undefined' && import.meta.env?.DEV;

export const SECURITY_CONFIG = {
  // Content Security Policy - Development vs Production
  csp: isDevelopment ? {
    // Development: Allow all sources for maximum flexibility (no eval)
    'default-src': ['*', "'unsafe-inline'", 'data:', 'blob:'],
    'script-src': ['*', "'unsafe-inline'"],
    'style-src': ['*', "'unsafe-inline'"],
    'img-src': ['*', 'data:', 'blob:'],
    'font-src': ['*'],
    'connect-src': ['*'],
    'frame-src': ['*'],
    'object-src': ['*'],
    'base-uri': ['*'],
    'form-action': ['*']
  } : {
    // Production: Restrictive CSP for security
    'default-src': ["'self'"],
    'script-src': [
      "'self'",
      "'unsafe-inline'", // Required for inline scripts
      'https://api.opencagedata.com',
      'https://nominatim.openstreetmap.org',
      'https://maps.googleapis.com',
      'https://www.googletagmanager.com',
      'https://www.google-analytics.com',
      'https://www.gstatic.com',
      'https://cdn.jsdelivr.net'
    ],
    'style-src': [
      "'self'",
      "'unsafe-inline'",
      'https://fonts.googleapis.com'
    ],
    'img-src': [
      "'self'",
      'data:',
      'https:',
      'blob:'
    ],
    'font-src': [
      "'self'",
      'https://fonts.gstatic.com'
    ],
    'connect-src': [
      "'self'",
      'https://api.opencagedata.com',
      'https://nominatim.openstreetmap.org',
      'https://maps.googleapis.com',
      'https://api.openrouteservice.org',
      'https://www.google-analytics.com',
      'https://analytics.google.com',
      'https://www.googletagmanager.com',
      'https://uhqaaexjvvmbpacuusac.supabase.co',
      'https://api.ipify.org',
      'https://ipapi.co',
      'https://ipinfo.io'
    ],
    'frame-src': ["'none'"],
    'object-src': ["'none'"],
    'base-uri': ["'self'"],
    'form-action': ["'self'"],
    'frame-ancestors': ["'none'"]
  },

  // Security Headers
  headers: {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=(self)',
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains; preload'
  },

  // Input Validation
  validation: {
    maxQueryLength: 100,
    maxSuggestions: 10,
    allowedCharacters: /^[a-zA-Z0-9\s\-.,#&()]+$/,
    sanitizePatterns: [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi
    ]
  },

  // Rate Limiting
  rateLimit: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    maxRequests: 100, // per window
    maxSuggestions: 50, // per window
    maxApiCalls: 1000 // per day
  },

  // API Security
  api: {
    timeout: 5000, // 5 seconds
    retryAttempts: 2,
    retryDelay: 1000, // 1 second
    maxConcurrentRequests: 5,
    allowedOrigins: [
      'http://localhost:4173',
      'http://localhost:4174',
      'http://localhost:4175',
      'http://localhost:4176',
      'https://yourdomain.com',
      'https://www.yourdomain.com'
    ]
  },

  // Data Protection
  dataProtection: {
    encryptSensitiveData: true,
    hashPasswords: true,
    saltRounds: 12,
    sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours
    maxLoginAttempts: 5,
    lockoutDuration: 15 * 60 * 1000 // 15 minutes
  },

  // Monitoring
  monitoring: {
    enableErrorTracking: true,
    enablePerformanceMonitoring: true,
    enableSecurityMonitoring: true,
    logLevel: import.meta.env.PROD ? 'error' : 'debug',
    maxLogEntries: 1000
  },

  // CORS Configuration
  cors: {
    origin: (origin, callback) => {
      const allowedOrigins = SECURITY_CONFIG.api.allowedOrigins;
      if (!origin || allowedOrigins.includes(origin)) {
        callback(null, true);
      } else {
        callback(new Error('Not allowed by CORS'));
      }
    },
    credentials: true,
    methods: ['GET', 'POST', 'PUT', 'DELETE', 'OPTIONS'],
    allowedHeaders: ['Content-Type', 'Authorization', 'X-Requested-With']
  }
};

export default SECURITY_CONFIG;